import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import type { SignSide } from '../../renderer/viewer/three/HoloBillboard'
import { HoloSignContext } from './holoSignContext'
import './HoloSignPortal.css'

/**
 * Synchronously read a holo-sign DOM element from the graphics backend.
 *
 * `getThreeJsRendererMethods()` wraps every call in an async Proxy, which is
 * useless for React portals (createPortal needs the target node now, not a
 * promise). `controls.ts` already bypasses that proxy by reading
 * `appViewer.backend.backendMethods` directly — we do the same here.
 */
const getHoloSignElementSync = (side: SignSide): HTMLDivElement | null => {
  try {
    const backend = (globalThis as any).appViewer?.backend
    if (backend?.id !== 'threejs' || !backend.backendMethods) return null
    const fn = backend.backendMethods.getHoloSignElement
    if (typeof fn !== 'function') return null
    const el = fn(side)
    return el instanceof HTMLDivElement ? el : null
  } catch {
    return null
  }
}

const callHoloMethod = (name: 'revealHoloSign' | 'setHoloBillboardVisible', ...args: any[]) => {
  try {
    const backend = (globalThis as any).appViewer?.backend
    if (backend?.id !== 'threejs' || !backend.backendMethods) return
    backend.backendMethods[name]?.(...args)
  } catch {}
}

/**
 * Poll until the backend has created the sign element. The graphics backend is
 * built lazily when the world starts, so on first replay mount the element may
 * not exist yet.
 */
function useHoloSignElement (side: SignSide, enabled: boolean): HTMLDivElement | null {
  const [el, setEl] = useState<HTMLDivElement | null>(() => getHoloSignElementSync(side))
  useEffect(() => {
    if (!enabled) return
    if (el) return
    let raf = 0
    const tick = () => {
      const found = getHoloSignElementSync(side)
      if (found) {
        setEl(found)
      } else {
        raf = requestAnimationFrame(tick)
      }
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [side, el, enabled])
  return enabled ? el : null
}

interface Props {
  side: SignSide
  enabled?: boolean
  children: ReactNode
}

/**
 * Portals React children into a hanging stone sign's HTML face in the shared
 * 3D scene. The sign element is created by `HoloBillboard` in the renderer and
 * exposed via the graphics backend. Once React mounts content into it, the face
 * is revealed (fades in) so an empty slab never flashes a blank face.
 *
 * The embedded panels keep their own `position: fixed` CSS, but because the
 * CSS3D content element carries a `transform`, the spec makes that element the
 * containing block for fixed descendants — so the panels position themselves
 * inside the sign face, not the viewport. The companion stylesheet
 * (`HoloSignPortal.css`) neutralises the fixed insets so each panel fills its
 * sign face edge-to-edge.
 */
export default function HoloSignPortal ({ side, enabled = true, children }: Props) {
  const el = useHoloSignElement(side, enabled)
  useEffect(() => {
    if (enabled && el) callHoloMethod('revealHoloSign', side)
  }, [side, el, enabled])
  if (!enabled) return <>{children}</>
  if (!el) return null
  // Mark the subtree as embedded so portaled-out panel pieces (memory/fps cards)
  // stay on the slab instead of escaping to document.body. Context propagates
  // through this createPortal via the React tree.
  return createPortal(<HoloSignContext.Provider value={side}>{children}</HoloSignContext.Provider>, el)
}
